import z from 'zod'
import { type TaskOriginId, TaskOriginIdSchema } from '../task'
import { type QuestionId, QuestionIdSchema } from '../question'
import {
  type AccountStatusId,
  AccountStatusIdSchema,
  type ProgramId,
  ProgramIdSchema,
  type WorkflowActionId,
  WorkflowActionIdSchema,
  type WorkflowStageId,
  WorkflowStageIdSchema,
} from '../ids'
import { ProgramTaskIdSchema } from '../program/programTask'
import { type GameId, GameIdSchema } from '../game/shared'
import { type CustomFieldId, CustomFieldIdSchema } from '../customField'
import { capitalizeWords } from '../../util/string'
import { pluralize } from '../../util/pluralize'
import {
  type AccountStatsByStatusKey,
  AccountStatsByStatusKeySchema,
  type AccountRole,
  AccountRoleSchema,
  ReportWorkflowFieldSchema,
  type WorkflowFieldEntity,
  WorkflowFieldEntitySchema,
  type ReportAccountWorkflowMetric,
  ReportAccountWorkflowMetricSchema,
  type ReportConversationMetric,
  ReportConversationMetricSchema,
  ReportConversationWorkflowFieldSchema,
  type ReportCustomFieldAccountDate,
  ReportCustomFieldAccountDateSchema,
  type ReportCustomFieldAggregationType,
  ReportCustomFieldAggregationTypeSchema,
  ReportGameFieldSchema,
  type ReportKpiType,
  ReportQuestionFieldSchema,
  type ReportQuestionMetric,
  ReportQuestionMetricSchema,
  type ReportProgramKpiMetric,
  ReportProgramKpiMetricSchema,
  type ReportTaskKpiMetric,
  ReportTaskKpiMetricSchema,
  type ReportTeamTaskKpiMetric,
  ReportTeamTaskKpiMetricSchema,
  TaskKpiIdSchema,
  type TaskKpiId,
  type ReportWorkflowActionMetric,
  ReportWorkflowActionMetricSchema,
} from './model'
import { type ReportDateRange, type ReportDateRangeUnit } from './config'

const ACCOUNT_REGEX = /^(?<role>[^.]+)\.(?<statusId>AS\.[^.]+(?:-[^.]+)?|[^.]+)\.(?<metric>AS\.[^.]+|[^.]+)$/
/** @deprecated use parseLegacyAccountKpi */
export function parseAccountKpi(pattern: string): { role: string; statusId: string; metric: string } | undefined {
  const match = ACCOUNT_REGEX.exec(pattern)
  if (match?.groups) {
    return {
      role: match.groups.role,
      statusId: match.groups.statusId,
      metric: match.groups.metric,
    }
  }
}

export function parseLegacyAccountKpi(pattern: string):
  | {
      role: AccountRole
      statusId: AccountStatusId
      metric: AccountStatsByStatusKey
    }
  | undefined {
  const match = ACCOUNT_REGEX.exec(pattern)
  if (!match?.groups) return undefined
  const role = AccountRoleSchema.safeParse(match.groups.role)
  const statusId = AccountStatusIdSchema.safeParse(match.groups.statusId)
  const metric = AccountStatsByStatusKeySchema.safeParse(match.groups.metric)
  if (!role.success || !statusId.success || !metric.success) return
  return {
    role: role.data,
    statusId: statusId.data,
    metric: metric.data,
  }
}

export function parseAccountWorkflowKpi(field: ReportKpiType):
  | {
      role: AccountRole
      stageId: WorkflowStageId
      metric: ReportAccountWorkflowMetric
    }
  | undefined {
  const [role, stageId, ...metric] = field.split('|')

  const parsedRole = AccountRoleSchema.safeParse(role)
  if (!parsedRole.success) return

  const parsedStageId = WorkflowStageIdSchema.safeParse(stageId)
  if (!parsedStageId.success) return

  const parsedMetric = ReportAccountWorkflowMetricSchema.safeParse(metric.join('|'))
  if (!parsedMetric.success) return

  return {
    role: parsedRole.data,
    stageId: parsedStageId.data,
    metric: parsedMetric.data,
  }
}

export function parseWorkflowField(
  field: ReportKpiType
): { entity: WorkflowFieldEntity; metric: string; id: string; param?: string } | undefined {
  const parse = ReportWorkflowFieldSchema.safeParse(field)
  if (!parse.success) return

  const [rawEntity, id, metric, param] = parse.data.split('|')
  const entity = WorkflowFieldEntitySchema.parse(rawEntity)
  return { entity, id, metric, param }
}

export function parseWorkflowActionKpi(field: ReportKpiType):
  | {
      actionId: WorkflowActionId
      metric: ReportWorkflowActionMetric
    }
  | undefined {
  const [_, actionId, metric] = field.split('|')

  const parsedActionId = WorkflowActionIdSchema.safeParse(actionId)
  if (!parsedActionId.success) return

  const parsedMetric = ReportWorkflowActionMetricSchema.safeParse(metric)
  if (!parsedMetric.success) return

  return {
    actionId: parsedActionId.data,
    metric: parsedMetric.data,
  }
}

export function parseTaskKpi(field: ReportKpiType):
  | {
      id: TaskKpiId
      metric: ReportTaskKpiMetric
    }
  | undefined {
  if (!field.startsWith('task|')) return
  if (field.startsWith('task|team|')) return
  const [, id, metric] = field.split('|')
  const parsedId = TaskKpiIdSchema.safeParse(id)
  if (!parsedId.success) return
  const parsedMetric = ReportTaskKpiMetricSchema.safeParse(metric)
  if (!parsedMetric.success) return
  return { id: parsedId.data, metric: parsedMetric.data }
}

export function parseTeamTaskKpi(field: ReportKpiType):
  | {
      id: TaskKpiId
      metric: ReportTeamTaskKpiMetric
    }
  | undefined {
  if (!field.startsWith('task|team|')) return
  const [, , id, metric] = field.split('|')
  const parsedId = TaskKpiIdSchema.safeParse(id)
  if (!parsedId.success) return
  const parsedMetric = ReportTeamTaskKpiMetricSchema.safeParse(metric)
  if (!parsedMetric.success) return
  return { id: parsedId.data, metric: parsedMetric.data }
}

export function parseProgramKpiV2(field: ReportKpiType):
  | {
      programId: ProgramId
      metric: ReportProgramKpiMetric
    }
  | undefined {
  if (!field.startsWith('program|')) return
  if (field.startsWith('program|team|')) return
  const [, programId, metric] = field.split('|')
  const parsedProgramId = ProgramIdSchema.safeParse(programId)
  if (!parsedProgramId.success) return
  const parsedMetric = ReportProgramKpiMetricSchema.safeParse(metric)
  if (!parsedMetric.success) return
  return { programId: parsedProgramId.data, metric: parsedMetric.data }
}

export function parseTeamProgramKpi(field: ReportKpiType):
  | {
      programId: ProgramId
      metric: ReportTeamTaskKpiMetric
    }
  | undefined {
  if (!field.startsWith('program|team|')) return
  const [, , programId, metric] = field.split('|')
  const parsedProgramId = ProgramIdSchema.safeParse(programId)
  if (!parsedProgramId.success) return
  const parsedMetric = ReportTeamTaskKpiMetricSchema.safeParse(metric)
  if (!parsedMetric.success) return
  return { programId: parsedProgramId.data, metric: parsedMetric.data }
}

/** @deprecated old format — kept for backward compat until migration completes */
export function parseProgramKpi(field: ReportKpiType):
  | {
      isTeam?: boolean
      id: ProgramId | TaskOriginId
    }
  | undefined {
  if (!field.startsWith('program.')) return

  const isTeam = field.includes('team')
  const id = getProgramIdFromKpi(field)
  const schema = z.union([ProgramIdSchema, TaskOriginIdSchema])
  const parsedId = schema.safeParse(id)
  if (!parsedId.success) return

  return {
    isTeam,
    id: parsedId.data,
  }
}

/** @deprecated old format — kept for backward compat until migration completes */
function getProgramIdFromKpi(field: string): string {
  if (field.includes('program.team.tasks.completedAt.')) {
    return field.replace('program.team.tasks.completedAt.', '')
  } else if (field.includes('program.team.tasks.')) return field.replace('program.team.tasks.', '')
  else if (field.includes('program.tasks.completedAt.')) return field.replace('program.tasks.completedAt.', '')
  else return field.replace('program.tasks.', '')
}

export function parseCustomFieldAccountKpi(kpi: ReportKpiType):
  | {
      role: AccountRole
      customFieldId: CustomFieldId
      agg: ReportCustomFieldAggregationType
      date: ReportCustomFieldAccountDate
    }
  | undefined {
  const [role, aggType, customFieldId, statusId] = kpi.replace('cf|', '').split('|')

  const parsedRole = AccountRoleSchema.safeParse(role)
  const parsedAggType = ReportCustomFieldAggregationTypeSchema.safeParse(aggType)
  const parsedCustomFieldId = CustomFieldIdSchema.safeParse(customFieldId)
  const parsedDate = ReportCustomFieldAccountDateSchema.safeParse(statusId)

  if (!parsedRole.success || !parsedAggType.success || !parsedCustomFieldId.success || !parsedDate.success) {
    return
  }

  return {
    role: parsedRole.data,
    customFieldId: parsedCustomFieldId.data,
    agg: parsedAggType.data,
    date: parsedDate.data,
  }
}

export type QuestionKpiParse =
  | { questionId: QuestionId; metric: ReportQuestionMetric }
  | { questionId: QuestionId; metric: 'choice'; choiceIndex: number }
  | { questionId: QuestionId; metric: 'choicePercent'; choiceIndex: number }

export function parseQuestionKpi(field: ReportKpiType): QuestionKpiParse | undefined {
  const { success, data } = ReportQuestionFieldSchema.safeParse(field)
  if (!success) return

  const parts = data.split('|')
  const parsedQuestionId = QuestionIdSchema.safeParse(parts[1])
  if (!parsedQuestionId.success) return

  if (parts.length === 4 && (parts[2] === 'choice' || parts[2] === 'choicePercent')) {
    const choiceIndex = Number(parts[3])
    if (Number.isNaN(choiceIndex)) return
    return { questionId: parsedQuestionId.data, metric: parts[2], choiceIndex }
  }

  const parsedMetric = ReportQuestionMetricSchema.safeParse(parts[2])
  if (!parsedMetric.success) return

  return { questionId: parsedQuestionId.data, metric: parsedMetric.data }
}

export function parseGameKpi(field: ReportKpiType): { gameId: GameId } | undefined {
  const { success, data } = ReportGameFieldSchema.safeParse(field)
  if (!success) return

  const [, gameId, metric] = data.split('|')

  const parsedGameId = GameIdSchema.safeParse(gameId)
  if (!parsedGameId.success) return

  return { gameId: parsedGameId.data }
}

export function parseConversationWorkflowKpi(field: ReportKpiType):
  | {
      stageId: WorkflowStageId
      metric: ReportConversationMetric
    }
  | undefined {
  const { success, data } = ReportConversationWorkflowFieldSchema.safeParse(field)
  if (!success) return

  const [, stageId, metric] = data.split('|')

  const parsedStageId = WorkflowStageIdSchema.safeParse(stageId)
  const parsedMetric = ReportConversationMetricSchema.safeParse(metric)
  if (!parsedStageId.success || !parsedMetric.success) return

  return {
    stageId: parsedStageId.data,
    metric: parsedMetric.data,
  }
}

export function labelForDateRange(d: ReportDateRange): string {
  if (d.type === 'preset') {
    return capitalizeWords(d.period.replace(/_/g, ' '))
  }
  if (d.type === 'relative') {
    return `Last ${d.value} ${pluralize(d.value, SINGULAR_DATE_RANGE_UNIT[d.unit])}`
  }
  if (d.type === 'fixed') return 'Custom range'
  if (d.type === 'advanced') return 'Advanced range'
  return 'Custom range'
}

const SINGULAR_DATE_RANGE_UNIT: Record<ReportDateRangeUnit, string> = {
  hours: 'hour',
  days: 'day',
  weeks: 'week',
  months: 'month',
  years: 'year',
}
